<template>
  <v-dialog v-model="dialog" scrollable persistent fullscreen hide-overlay>
    <v-card>
      <v-toolbar color="primary" dark tabs>
        <v-toolbar-title>
          {{
            user._id
              ? $t('titles.global.updateModel', { field: $tc('models.user') })
              : $t('titles.global.newModel', { field: $tc('models.user') })
          }}
        </v-toolbar-title>
        <v-spacer />

        <button-confirm
          v-if="user._id"
          v-ripple
          color="white"
          color-text="primary"
          dark
          small
          flat
          icon="exit_to_app"
          :text="$t('titles.users.deleteSessions')"
          @submit="deleteUserSession(user.email)"
        />
        <v-btn dark icon @click="cancel">
          <v-icon>close</v-icon>
        </v-btn>
        <v-tabs
          slot="extension"
          v-model="tab"
          color="primary"
          slider-color="white"
          centered
        >
          <v-tab>{{ $t('titles.users.personalData') }}</v-tab>
          <template v-if="isAdmin || user._id !== authUser._id">
            <v-tab :disabled="!user._id">
              {{ $tc('models.role', 2) }}
            </v-tab>
          </template>
          <v-tab :disabled="!user._id">
            {{ $tc('models.notification', 2) }}
          </v-tab>
          <template v-if="isAdmin || canSetSubscription">
            <v-tab :disabled="!user._id">
              {{ $tc('models.subscription', 2) }}
            </v-tab>
          </template>
          <v-tab :disabled="!user._id">
            {{ $t('titles.global.followings') }}
          </v-tab>
          <template v-if="isPlayer">
            <v-tab>{{ $t('titles.global.profile') }}</v-tab>
            <v-tab>{{ $t('titles.users.legalTutors') }}</v-tab>
            <v-tab>{{ $t('titles.global.galery') }}</v-tab>
          </template>
        </v-tabs>
      </v-toolbar>
      <v-card-text>
        <v-tabs-items v-model="tab">
          <v-tab-item ripple>
            <step-information
              ref="stepInformation"
              :user.sync="user"
              :file.sync="file"
              :is-player="isPlayer"
            />
          </v-tab-item>
          <template v-if="isAdmin || user._id !== authUser._id">
            <v-tab-item ripple>
              <step-roles
                :id="user._id"
                :type-role="typeRole"
                :entity-id="entityId"
              />
            </v-tab-item>
          </template>
          <v-tab-item v-if="user._id" ripple>
            <tab-notifications :id="user._id" :email="user.email" />
          </v-tab-item>
          <template v-if="(isAdmin || canSetSubscription) && user._id">
            <v-tab-item ripple>
              <tab-subscriptions :id="user._id" :email="user.email" />
            </v-tab-item>
          </template>
          <v-tab-item ripple>
            <tab-followings :id="user._id" />
          </v-tab-item>
          <template v-if="isPlayer">
            <v-tab-item ripple>
              <tab-profile ref="tabProfile" :profile="profile" />
            </v-tab-item>
            <v-tab-item ripple>
              <user-list
                type-role="familiar"
                :player-id="user._id"
                :title="$t('titles.users.legalTutors')"
              />
            </v-tab-item>
            <v-tab-item ripple>
              <tab-galery :galery="galery" />
            </v-tab-item>
          </template>
        </v-tabs-items>
      </v-card-text>

      <v-card-actions>
        <span style="color: red">*</span>
        <span>{{ $t('action.messages.fieldRequired') }}</span>
        <v-spacer />
        <!-- <v-btn
          v-if="step == 2"
          @click="step = 1"
          color="primary"
          small
        >{{$t('titles.global.previous')}}</v-btn>-->
        <meta-button type="reset" :disabled="loading" @click="clear" />
        <template v-if="tab == 0">
          <meta-button
            v-if="!user._id"
            type="create"
            :loading="loading"
            @click="submit()"
          />
          <meta-button
            v-else
            type="update"
            :loading="loading"
            @click="submit()"
          />
        </template>
        <!-- <v-btn v-if="step == 1" @click="step = 2" color="success" small>{{$t('titles.global.next')}}</v-btn> -->
        <meta-button type="cancel" @click="cancel" />
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions, mapState } from 'vuex'
//import StepInformation from './StepInformation'
const StepInformation = () => import('@/components/management/users/StepInformation.vue')
import StepRoles from './StepRoles'
import TabFollowings from './TabFollowings'
import TabGalery from './TabGalery'
import TabNotifications from './TabNotifications'
import TabProfile from './TabProfile'
import TabSubscriptions from './TabSubscriptions'
import UserList from '@/components/management/entities/entity-users/components/EntityUserList.vue'

import { parseObject } from '@/helpers/utils'

export default {
  name: 'UserForm',
  components: {
    'step-information': StepInformation, // registrazione esplicita
    StepRoles,
    TabProfile,
    TabGalery,
    TabFollowings,
    TabSubscriptions,
    TabNotifications,
    UserList,
  },
  props: {
    dialog: {
      type: Boolean,
      default: false,
    },
    entityId: {
      type: String,
    },
    typeRole: {
      type: String,
    },
  },
  data() {
    return {
      user: {},
      galery: [],
      tab: 0,
      file: '',
    }
  },
  watch: {
    dialog(val) {
      if (!val) this.cancel()
    },
    selected: {
      handler: function (val) {
        if (val) this.setObject(val)
      },
      immediate: true,
      deep: true,
    },
  },
  mounted() {
    this.setObject(this.selected)
  },
  computed: {
    ...mapState({
      selected: (state) => state.users.selected,
      loading: (state) => state.users.loading,
      userRoles: (state) => state.users.userRoles,
    }),
    isPlayer() {
      return this.userRoles.some((role) => role.type === 'player')
    },
    canSetSubscription() {
      return this.authUser.roles.some(
        (role) =>
          role.roleId.id === '62e829b6868951330cfdab48' ||
          role.roleId.id === '5ebdd4ab147d097f81ca2c6a'
      )
    },
  },
  methods: {
    ...mapActions({
      setCreate: 'users/create',
      setUpdate: 'users/update',
      fetchOne: 'users/fetchOne',
      reset: 'users/reset',
      uploadFile: 'files/uploadPush',
      deleteUserSession: 'users/deleteUserSession',
    }),
    submit() {
      Promise.all([this.$refs.stepInformation.validate()]).then(
        async (valid) => {
          if (valid.some((valid) => !valid)) return
          try {
            let payload = Object.assign({}, this.user)
            let result
            if (!payload._id) {
              if (!this.isAdmin) payload.isAccountDisabled = true
              payload.register = 'backoffice'
              result = await this.setCreate(payload)
            } else {
              result = await this.setUpdate(payload)
            }
            if (result) {
              if (this.file) {
                await this.uploadFile({
                  id: result._id,
                  model: 'Users',
                  field: 'photo',
                  file: this.file,
                })
                this.file = ''
              }
              this.fetchOne(result._id)
            }
          } catch (error) {
            console.log('Error al crear/actualizar usuario.')
          }
        }
      )
    },
    setObject(user) {
      this.user = parseObject(user)
      this.clearObject()
      this.profile = {}
      this.galery = []

      if (user.playerProfile) {
        this.profile = Object.assign({}, user.playerProfile)
      }
      if (Array.isArray(user.playerGalery)) {
        this.galery = user.playerGalery.slice()
      }
    },
    cancel() {
      this.$emit('close')
      this.clear()
    },
    clear() {
      this.$nextTick(() => {
        this.reset()
        this.tab = 0
        this.roles = []
        this.profile = {}
        this.galery = []
        if (this.$refs.stepInformation) this.$refs.stepInformation.clear()
        if (this.$refs.tabProfile) this.$refs.tabProfile.clear()
      })
    },
    clearObject() {
      delete this.user.roles
      delete this.user.username
      delete this.user.lastAccess
      delete this.user.tokens
      delete this.user.register
      delete this.user.devices
      delete this.user.following
      delete this.user.roles
      delete this.user.playerGalery
      delete this.user.playerProfile
      delete this.user.trialMonth
      delete this.user.cards
    },
  },
}
</script>
